import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useState } from "react"
import {
  Users, TrendingUp, Activity, MessageSquare, Timer, User, UserCheck,
  ArrowUp, ArrowDown, LineChart, Pin, PinOff
} from "lucide-react"
import { getConfidenceBand, BANDS } from "../shared/ConfidenceBandBanner"
import { BusinessHoursPopover } from "../shared/BusinessHoursPopover"
import { DEFAULT_BUSINESS_HOURS, type BusinessHoursConfig } from "../../utils/analyticsUtils"
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine,
  PieChart, Pie, BarChart, Bar, Cell, Legend, ComposedChart, Line, Tooltip
} from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { InfoTooltip } from "../shared/InfoTooltip"
import { CHART_CONFIG, CHART_COLORS } from "../../utils/constants"
import { formatDuration } from "../../utils/formatting"
import { ExportDialog, ExportButton, type ExportColumn } from "../shared/ExportDialog"
import { VarBreakdownChart } from "../shared/VarBreakdownChart"
import type { OverviewAnalytics, DailyTrend } from "../../hooks/useAnalytics"
import type { PinnedVarBreakdown } from "../../hooks/useVariablesAnalytics"
import type { FormFieldMeta } from "./VariablesTab"

interface StatusBreakdown {
  status: string
  count: number
}

interface OverviewTabProps {
  overviewAnalytics: OverviewAnalytics
  dailyTrends: DailyTrend[]
  statusBreakdown: StatusBreakdown[]
  pinnedBreakdowns?: PinnedVarBreakdown[]
  onUnpinVar?: (key: string) => void
  onNavigateToVariables?: () => void
  ratingTotal?: number
  formFieldMeta?: Record<string, FormFieldMeta>
  businessHours?: BusinessHoursConfig
  onBusinessHoursChange?: (cfg: BusinessHoursConfig) => void
}

export function OverviewTab({
  overviewAnalytics,
  dailyTrends,
  statusBreakdown,
  pinnedBreakdowns = [],
  onUnpinVar,
  onNavigateToVariables,
  ratingTotal = 0,
  formFieldMeta = {},
  businessHours = DEFAULT_BUSINESS_HOURS,
  onBusinessHoursChange,
}: OverviewTabProps) {
  const confidenceBand = getConfidenceBand(overviewAnalytics.thumbsUpRate, ratingTotal)
  const bandInfo = confidenceBand ? BANDS[confidenceBand] : null
  const [exportOpen, setExportOpen] = useState(false)

  const pinTitle = (key: string): string => {
    const meta = formFieldMeta[key]
    if (meta?.title) return meta.title
    return key.startsWith("form.") ? key.slice("form.".length) : key
  }
  const pinValueLabel = (key: string, value: string): string => {
    const labels = formFieldMeta[key]?.optionLabels
    return (labels && labels[value]) || value
  }

  const trendColumns: ExportColumn[] = [
    { key: 'date', label: 'Date' },
    { key: 'incoming', label: 'Incoming Messages' },
    { key: 'outgoing', label: 'Outgoing Messages' },
    { key: 'newUsers', label: 'New Users' },
    { key: 'activeUsers', label: 'Active Users' },
    { key: 'cumulativeUsers', label: 'Cumulative Users' },
  ]

  const trendData = dailyTrends.map(t => ({
    date: t.date,
    incoming: t.incoming,
    outgoing: t.outgoing,
    newUsers: t.newUsers,
    activeUsers: t.activeUsers,
    cumulativeUsers: t.cumulativeUsers,
  }))

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportButton onClick={() => setExportOpen(true)} label="Export Trends" />
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        title="Export Overview Data"
        description="Export daily trend data as CSV"
        columns={trendColumns}
        data={trendData}
        filename="overview_daily_trends"
      />

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-linear-to-br from-blue-500 to-blue-600 text-white border-0 min-h-[100px]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-blue-100 uppercase tracking-wide flex items-center gap-1">
                  Total Users
                  <InfoTooltip text="All unique contacts who have interacted with your service during the selected date range" className="text-blue-200" />
                </div>
                <div className="text-3xl font-bold mt-1">{overviewAnalytics.totalContacts.toLocaleString()}</div>
              </div>
              <Users className="h-8 w-8 text-blue-200 shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-linear-to-br from-pink-500 to-pink-600 text-white border-0 min-h-[100px]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-orange-100 uppercase">
                  Messages
                  <InfoTooltip text="Total number of messages sent and received during the selected date range" className="text-orange-200" />
                </div>
                <div className="text-3xl font-bold">{overviewAnalytics.totalMessages}</div>
              </div>
              <MessageSquare className="h-8 w-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-linear-to-br from-pink-500 to-pink-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-pink-100 uppercase">
                  Avg Response
                  <InfoTooltip text="Median time between receiving a user message and sending a reply (less affected by outliers)" className="text-pink-200" />
                </div>
                <div className="text-3xl font-bold">{formatDuration(overviewAnalytics.medianResponseTime)}</div>
              </div>
              <Timer className="h-8 w-8 text-pink-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-linear-to-br from-violet-500 to-violet-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-violet-100 uppercase flex items-center">
                  {businessHours.aggregation === "average" ? "Avg" : "Median"} Moderator Response
                  <InfoTooltip text="Time a user waited for a moderator, measured from their last message to the moderator's first reply. Consecutive moderator messages count once. Bot replies in between are ignored." className="text-violet-200" />
                  {onBusinessHoursChange && (
                    <BusinessHoursPopover
                      value={businessHours}
                      onChange={onBusinessHoursChange}
                      className="text-violet-100 ml-0.5"
                    />
                  )}
                </div>
                <div className="text-3xl font-bold">
                  {overviewAnalytics.moderatorResponseCount > 0
                    ? formatDuration(overviewAnalytics.medianModeratorResponseTime)
                    : 'N/A'}
                </div>
                <div className="text-xs text-violet-100 mt-0.5">
                  {overviewAnalytics.moderatorResponseCount > 0
                    ? `${overviewAnalytics.moderatorResponseCount} replies`
                    : 'No moderator replies'}
                  {businessHours.enabled && ' · business hours'}
                  {businessHours.scope === 'inside' && ' · in hours'}
                  {businessHours.scope === 'outside' && ' · out of hours'}
                </div>
              </div>
              <UserCheck className="h-8 w-8 text-violet-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-linear-to-br from-teal-500 to-teal-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-teal-100 uppercase flex items-center">
                  {businessHours.aggregation === "average" ? "Avg" : "Median"} Effective Response
                  <InfoTooltip text="How long users waited for the answer that counted: the moderator's reply where a human responded, the bot's where the bot handled it. A bot message before a human reply does not stop the clock." className="text-teal-200" />
                  {onBusinessHoursChange && (
                    <BusinessHoursPopover
                      value={businessHours}
                      onChange={onBusinessHoursChange}
                      className="text-teal-100 ml-0.5"
                    />
                  )}
                </div>
                <div className="text-3xl font-bold">
                  {overviewAnalytics.effectiveResponseCount > 0
                    ? formatDuration(overviewAnalytics.effectiveResponseTime)
                    : 'N/A'}
                </div>
                <div className="text-xs text-teal-100 mt-0.5">
                  {overviewAnalytics.effectiveResponseCount > 0
                    ? `${overviewAnalytics.effectiveHumanCount} human · ${overviewAnalytics.effectiveBotCount} bot`
                    : 'No responses'}
                  {businessHours.scope === 'inside' && ' · in hours'}
                  {businessHours.scope === 'outside' && ' · out of hours'}
                </div>
              </div>
              <Timer className="h-8 w-8 text-teal-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-linear-to-br from-amber-500 to-amber-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-amber-100 uppercase">
                  Feedback
                  <InfoTooltip text="Thumbs up/down feedback from users on bot responses" className="text-amber-200" />
                </div>
                <div className="text-2xl font-bold flex items-center gap-2">
                  <span className="flex items-center gap-1">👍 {overviewAnalytics.thumbsUp}</span>
                  <span className="flex items-center gap-1">👎 {overviewAnalytics.thumbsDown}</span>
                </div>
                {bandInfo && (
                  <Badge className={`mt-1 text-[10px] font-semibold border ${bandInfo.badgeCls}`}>
                    {bandInfo.label} Band
                  </Badge>
                )}
              </div>
              <div className="text-2xl">{overviewAnalytics.thumbsUpRate !== 'N/A' ? `${overviewAnalytics.thumbsUpRate}%` : '-'}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase">
                  Incoming
                  <InfoTooltip text="Messages received from users" />
                </div>
                <div className="text-2xl font-bold text-blue-600">{overviewAnalytics.incomingMessages}</div>
              </div>
              <ArrowDown className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase">
                  Outgoing
                  <InfoTooltip text="Messages sent to users (includes both bot and human responses)" />
                </div>
                <div className="text-2xl font-bold text-green-600">{overviewAnalytics.outgoingMessages}</div>
              </div>
              <ArrowUp className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase">
                  Human Handled
                  <InfoTooltip text="Conversations currently flagged as handled by a human operator" />
                </div>
                <div className="text-2xl font-bold text-amber-600">{overviewAnalytics.humanHandled}</div>
              </div>
              <User className="h-6 w-6 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase">
                  Human Takeovers
                  <InfoTooltip text="Distinct conversations where a human operator took over and sent at least one response (HITL). Counts conversations, not messages." />
                </div>
                <div className="text-2xl font-bold text-rose-600">{overviewAnalytics.humanTakeovers}</div>
              </div>
              <UserCheck className="h-6 w-6 text-rose-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        {/* Message Volume Trend */}
        <Card className="min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <LineChart className="h-5 w-5" />
              Message Volume Trend
              <InfoTooltip text="Daily count of incoming and outgoing messages over time. Helps identify usage patterns and trends." />
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <ChartContainer config={CHART_CONFIG} className="h-[300px] w-full min-w-0 aspect-auto">
              <AreaChart data={dailyTrends}>
                <defs>
                  <linearGradient id="colorIncoming" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorOutgoing" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="incoming" stroke="#3b82f6" fillOpacity={1} fill="url(#colorIncoming)" name="Incoming" />
                <Area type="monotone" dataKey="outgoing" stroke="#10b981" fillOpacity={1} fill="url(#colorOutgoing)" name="Outgoing" />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* User Growth */}
        <Card className="min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              User Growth (Cumulative)
              <InfoTooltip text="Running total of all users over time. Shows how your user base has grown." />
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <ChartContainer config={CHART_CONFIG} className="h-[300px] w-full min-w-0 aspect-auto">
              <AreaChart data={dailyTrends}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="cumulativeUsers" stroke="#ec4899" fillOpacity={1} fill="url(#colorUsers)" name="Total Users" />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Feedback Rate Over Time */}
      <Card className="min-w-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            Feedback Score Over Time
            <InfoTooltip text="Daily moderator feedback score (% positive ratings). Confidence band thresholds shown as reference lines: 95% High→Medium, 80% Medium→Low, 65% Low→Fail." />
          </CardTitle>
        </CardHeader>
        <CardContent className="min-w-0">
          {dailyTrends.some(d => d.feedbackRate !== null) ? (
            <ChartContainer config={CHART_CONFIG} className="h-[300px] w-full min-w-0 aspect-auto">
              <ComposedChart data={dailyTrends}>
                <defs>
                  <linearGradient id="colorFeedback" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis domain={[0, 100]} className="text-xs" tickFormatter={(v) => `${v}%`} width={38} />
                <ChartTooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null
                    const d = payload[0]?.payload
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-xs text-xs space-y-1">
                        <div className="font-medium">{label}</div>
                        {d.feedbackRate !== null
                          ? <div>Feedback: <span className="font-semibold">{d.feedbackRate}%</span></div>
                          : <div className="text-muted-foreground">No ratings</div>
                        }
                        <div className="text-muted-foreground">{d.ratedCount} rated response{d.ratedCount !== 1 ? 's' : ''}</div>
                      </div>
                    )
                  }}
                />
                {/* Band threshold reference lines */}
                <ReferenceLine y={95} stroke="#22c55e" strokeDasharray="4 3" label={{ value: "95% High", position: "insideTopRight", fontSize: 10, fill: "#16a34a" }} />
                <ReferenceLine y={80} stroke="#f59e0b" strokeDasharray="4 3" label={{ value: "80% Medium", position: "insideTopRight", fontSize: 10, fill: "#d97706" }} />
                <ReferenceLine y={65} stroke="#f97316" strokeDasharray="4 3" label={{ value: "65% Low", position: "insideTopRight", fontSize: 10, fill: "#ea580c" }} />
                <Area
                  type="monotone"
                  dataKey="feedbackRate"
                  stroke="#f59e0b"
                  fill="url(#colorFeedback)"
                  strokeWidth={2}
                  name="Feedback %"
                  connectNulls={true}
                  dot={(props: any) => {
                    const { cx, cy, payload } = props
                    if (payload.feedbackRate === null) return <g key={props.key} />
                    return <circle key={props.key} cx={cx} cy={cy} r={3} fill="#f59e0b" stroke="#fff" strokeWidth={1} />
                  }}
                />
              </ComposedChart>
            </ChartContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">
              No feedback ratings in this date range
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Row - Pie Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-w-0">
        {/* Message Direction */}
        <Card className="min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Message Direction
              <InfoTooltip text="Breakdown of messages sent vs received" />
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <ChartContainer config={CHART_CONFIG} className="h-[200px] w-full min-w-0 aspect-auto">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Incoming', value: overviewAnalytics.incomingMessages, fill: '#3b82f6' },
                    { name: 'Outgoing', value: overviewAnalytics.outgoingMessages, fill: '#10b981' }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Handler Type */}
        <Card className="min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Handled By
              <InfoTooltip text="Conversations handled by bot vs human operators" />
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <ChartContainer config={CHART_CONFIG} className="h-[200px] w-full min-w-0 aspect-auto">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Bot', value: overviewAnalytics.botHandled, fill: '#8b5cf6' },
                    { name: 'Human', value: overviewAnalytics.humanHandled, fill: '#f59e0b' }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Conversation Status */}
        <Card className="min-w-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Conversation Status
              <InfoTooltip text="Current status of all conversations (active, closed, etc.)" />
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            <ChartContainer config={CHART_CONFIG} className="h-[200px] w-full min-w-0 aspect-auto">
              <PieChart>
                <Pie
                  data={statusBreakdown.map((s, i) => ({ ...s, fill: CHART_COLORS[i % CHART_COLORS.length] }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  dataKey="count"
                  nameKey="status"
                  //@ts-ignore
                  label={({ status, count }) => `${status}: ${count}`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Pinned Variable Charts */}
      {pinnedBreakdowns.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <Pin className="h-3.5 w-3.5 text-primary" />
              Pinned Variables
            </h3>
            {onNavigateToVariables && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onNavigateToVariables}>
                Manage pins →
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {pinnedBreakdowns.map(({ key, breakdown, config }) => {
              return (
                <Card key={key} className="min-w-0">
                  <CardHeader className="pb-2 pt-3 px-4 flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium truncate" title={key}>{pinTitle(key)}</CardTitle>
                    {onUnpinVar && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive shrink-0" onClick={() => onUnpinVar(key)} title="Unpin">
                        <PinOff className="h-3 w-3" />
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="px-4 pb-3 min-w-0">
                    <VarBreakdownChart breakdown={breakdown} chartType={config.chartType} labelFor={v => pinValueLabel(key, v)} />
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}

      {/* Empty state when no pins */}
      {pinnedBreakdowns.length === 0 && onNavigateToVariables && (
        <div className="flex items-center justify-center py-6 border border-dashed rounded-lg text-muted-foreground">
          <div className="text-center space-y-2">
            <Pin className="h-6 w-6 mx-auto opacity-30" />
            <p className="text-sm">Pin variables from the Variables tab to see them here</p>
            <Button variant="ghost" size="sm" onClick={onNavigateToVariables}>
              Go to Variables →
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
